import { useDebouncedCallback, useDebouncedState } from "@mantine/hooks"
import { addEdge, useReactFlow, XYPosition } from "@xyflow/react"
import { useEffect, useState } from "react"
import useHistoryState from "./useHistoryState"
import { getLayoutedElements } from "./scenario.helper"
import { generateNewNodeId, getNewEdgeProps, isWithinMinDistance, transformFlowNodesAndEdgesToScenarioNodes } from "./components/flow.utils"
import { store } from "@/stores/stores"
import { nanoid } from "nanoid"
import { BaseNodeType } from "./components/nodes/Base.node"
import { BaseEdgeType } from "./components/Edge"
import { toast } from "@/lib/toast"
import { useSnapshot } from "valtio"
import { scenarioNodeSchema } from "@/gen/zod"
import { z } from "zod"
import { extractZodErrorMessage } from "@/lib/utils"
import { NodeDefinitionCategoryEnum, ScenarioNodeOnErrorEnum } from "@/gen"
import { NodeCategory } from "@/nodes"

interface useScenarioHelpersProps {
  nodes: BaseNodeType[]
  edges: any[]
  setEdges: any
  setNodes: any
  // form: UseFormReturnType<Zod.infer<typeof zScenarioUpdate>>
  id?: string
}

function useScenarioHelpers(props: useScenarioHelpersProps) {
  const snap = useSnapshot(store)
  const { setCenter, getNode, screenToFlowPosition } = useReactFlow()
  const [inited, setInited] = useDebouncedState(false, 200)
  const [drafted, setDrafted] = useState(false)

  // History State
  const history = useHistoryState({
    onUndoOrRedo: (elements) => {
      props.setEdges(elements.edges)
      props.setNodes(elements.nodes)
    },
  })

  // ███████╗███████╗███████╗███████╗ ██████╗████████╗███████╗
  // ██╔════╝██╔════╝██╔════╝██╔════╝██╔════╝╚══██╔══╝██╔════╝
  // █████╗  █████╗  █████╗  █████╗  ██║        ██║   ███████╗
  // ██╔══╝  ██╔══╝  ██╔══╝  ██╔══╝  ██║        ██║   ╚════██║
  // ███████╗██║     ██║     ███████╗╚██████╗   ██║   ███████║
  // ╚══════╝╚═╝     ╚═╝     ╚══════╝ ╚═════╝   ╚═╝   ╚══════╝
  //
  useEffect(() => {
    if (inited) {
      // When nodes or edges changed
      if (!drafted) {
        setDrafted(true)
      }
    } else {
      // Initial setup after nodes monted
      // Center initial node
      const triggerNode = props.nodes.find((node) => {
        const actionDef = store.scenario.nodeDefinitions.record[node.data.node_type]
        return actionDef.category === NodeDefinitionCategoryEnum.trigger
      })
      history.push({ nodes: props.nodes, edges: props.edges })
      setCenter(triggerNode?.position?.x || 0, triggerNode?.position?.y || 0, { duration: 0, zoom: 1 })
      // store.scenario.editing.lastNodeId = props.lastNodeId
      setInited(true)
    }
  }, [props.nodes, props.edges])

  useEffect(() => {
    if (store.scenario.editing.duplicatedNodes?.length) {
      const newNodes = store.scenario.editing.duplicatedNodes?.map((node) => {
        const newNode = mountNewNode(
          {
            position: {
              x: node.position.x + Math.floor(Math.random() * (65 - 55 + 1)) + 55,
              y: node.position.y + Math.floor(Math.random() * (65 - 55 + 1)) + 55,
            },
            actionType: node.data.node_type,
          }
        )
        return { ...newNode, type: node.type, data: node.data }
      })

      props.setNodes((nds) => nds.concat(newNodes))
      store.scenario.editing.duplicatedNodes = null
    }
  }, [snap.scenario.editing.duplicatedNodes])

  // ██╗  ██╗███████╗██╗     ██████╗ ███████╗██████╗     ███████╗██╗   ██╗███╗   ██╗ ██████╗████████╗██╗ ██████╗ ███╗   ██╗███████╗
  // ██║  ██║██╔════╝██║     ██╔══██╗██╔════╝██╔══██╗    ██╔════╝██║   ██║████╗  ██║██╔════╝╚══██╔══╝██║██╔═══██╗████╗  ██║██╔════╝
  // ███████║█████╗  ██║     ██████╔╝█████╗  ██████╔╝    █████╗  ██║   ██║██╔██╗ ██║██║        ██║   ██║██║   ██║██╔██╗ ██║███████╗
  // ██╔══██║██╔══╝  ██║     ██╔═══╝ ██╔══╝  ██╔══██╗    ██╔══╝  ██║   ██║██║╚██╗██║██║        ██║   ██║██║   ██║██║╚██╗██║╚════██║
  // ██║  ██║███████╗███████╗██║     ███████╗██║  ██║    ██║     ╚██████╔╝██║ ╚████║╚██████╗   ██║   ██║╚██████╔╝██║ ╚████║███████║
  // ╚═╝  ╚═╝╚══════╝╚══════╝╚═╝     ╚══════╝╚═╝  ╚═╝    ╚═╝      ╚═════╝ ╚═╝  ╚═══╝ ╚═════╝   ╚═╝   ╚═╝ ╚═════╝ ╚═╝  ╚═══╝╚══════╝
  //
  // const syncForm = useDebouncedCallback(async () => {
  //   props.form.setValues((prev) => ({ ...prev, nodes: props.nodes as ScenarioNode[], edges: props.edges }))
  // }, 500)

  const syncHistory = useDebouncedCallback(async () => {
    history.push({ nodes: props.nodes, edges: props.edges })
  }, 300)

  const onLayout = () => {
    const { nodes: layoutedNodes, edges: layoutedEdges } = getLayoutedElements(props.nodes, props.edges, "LR")

    props.setNodes([...layoutedNodes])
    props.setEdges([...layoutedEdges])
  }

  const handleDeselectAllNodes = () => {
    props.setNodes((nds) => nds.map((node) => ({ ...node, selected: false })))
  }

  const getSafeLabel = (name: string, nodeId?: string) => {
    let unique = false
    let newName = name
    let counter = 1
    while (!unique) {
      unique = !props.nodes.some((node) => node.data.label === newName && node.id !== nodeId)
      if (!unique) {
        newName = `${name} ${counter}`
        counter++
      }
    }
    return newName
  }


  const mountNewNode = (params: { position: XYPosition; nodeType: string; actionId: string; operationId: string }) => {
    const actionDef = store.scenario.nodeDefinitions.record[params.nodeType]

    const newNode: BaseNodeType = {
      id: generateNewNodeId(),
      type: "base",
      position: params.position,
      data: {
        action: `${params.actionId}::${params.operationId}`,
        node_type: params.nodeType,
        label: getSafeLabel(actionDef.label),
        // properties: actionDef.schema ? init(actionDef.schema) : {},
        properties: actionDef.default_properties_values || {},
        meta_properties: {},
        retry_on_fail: false,
        max_retries: 2,
        retry_interval: 1000,
        on_error: ScenarioNodeOnErrorEnum.stop,
      },
    }

    return newNode
  }

  // ██████╗ ██████╗ ███████╗██████╗  █████╗ ██████╗ ███████╗
  // ██╔══██╗██╔══██╗██╔════╝██╔══██╗██╔══██╗██╔══██╗██╔════╝
  // ██████╔╝██████╔╝█████╗  ██████╔╝███████║██████╔╝█████╗  
  // ██╔═══╝ ██╔══██╗██╔══╝  ██╔═══╝ ██╔══██║██╔══██╗██╔══╝  
  // ██║     ██║  ██║███████╗██║     ██║  ██║██║  ██║███████╗
  // ╚═╝     ╚═╝  ╚═╝╚══════╝╚═╝     ╚═╝  ╚═╝╚═╝  ╚═╝╚══════╝
  //                                                         
  const preparePublish = () => {
    // Find triggers
    if (!props.nodes.find((node) => {
      const nodeDef = store.scenario.nodeDefinitions.record[node.data.node_type]
      return nodeDef.category === NodeCategory.TRIGGER || (node.data.action as string)?.startsWith("trigger::")
    })) {
      toast.error("You must have at least one trigger")
      throw new Error("You must have at least one trigger")
    }

    const invalidNode = props.nodes.find((node) => !!node.data.invalid_tip)
    if (invalidNode) {
      toast.error("Your action has a problem. Fix it and try again.")
      setCenter(invalidNode.position.x - 40, invalidNode.position.y - 40, { duration: 300, zoom: 1 })
      store.scenario.actions.setHighlightNode(invalidNode.id)
      throw new Error("Your action has a problem. Fix it and try again.")
    }

    if (!props.id) {
      throw new Error("Scenario ID is required")
    }

    const scenariosNodes = transformFlowNodesAndEdgesToScenarioNodes(props.nodes, props.edges)
    const parsed = z.array(scenarioNodeSchema).safeParse(scenariosNodes)

    if (!parsed.success) {
      toast.error(extractZodErrorMessage(parsed.error))
      return
    }

    return scenariosNodes
  }

  // ██████╗ ██╗   ██╗██████╗ ██╗     ██╗███████╗██╗  ██╗
  // ██╔══██╗██║   ██║██╔══██╗██║     ██║██╔════╝██║  ██║
  // ██████╔╝██║   ██║██████╔╝██║     ██║███████╗███████║
  // ██╔═══╝ ██║   ██║██╔══██╗██║     ██║╚════██║██╔══██║
  // ██║     ╚██████╔╝██████╔╝███████╗██║███████║██║  ██║
  // ╚═╝      ╚═════╝ ╚═════╝ ╚══════╝╚═╝╚══════╝╚═╝  ╚═╝
  //
  const handlePublish = async (version_name: string) => {
    // console.log(props.form.getValues())
    // const errors = props.form.validate()

    // console.log(errors)
    // if (errors.hasErrors) {
    //   toast.error("Please fix the errors and try again before publishing.")
    //   throw new Error(errors.errors?.[0]?.toString())
    // }

    const scenariosNodes = preparePublish()

    // No errors
    return store.scenario.actions.update(props.id!, {
      version_name,
      nodes: scenariosNodes
    }).then((res) => {
      setDrafted(false)
      toast.success("Your scenario has been published")
      return res
    })
  }

  //   ██████╗ ██╗  ██╗ ██████╗ ███████╗████████╗
  //  ██╔════╝ ██║  ██║██╔═══██╗██╔════╝╚══██╔══╝
  //  ██║  ███╗███████║██║   ██║███████╗   ██║
  //  ██║   ██║██╔══██║██║   ██║╚════██║   ██║
  //  ╚██████╔╝██║  ██║╚██████╔╝███████║   ██║
  //   ╚═════╝ ╚═╝  ╚═╝ ╚═════╝ ╚══════╝   ╚═╝
  //
  const createGhostNode = (dto: { position: XYPosition; fromNode: BaseNodeType; fromHandle: any }) => {
    const id = `ghost`

    const targetPos = screenToFlowPosition({
      x: dto.position.x,
      y: dto.position.y,
    })

    const errorEdge = dto.fromHandle.id === "error_handle"

    const newNode: BaseNodeType = {
      id,
      type: "ghost",
      // position: getPositionWithMinDistance(dto.fromNode.position.x, dto.fromNode.position.y, targetPos.x, targetPos.y, 200),
      position: isWithinMinDistance(dto.fromNode.position.x, dto.fromNode.position.y, targetPos.x, targetPos.y, 150)
        ? {
          x: dto.fromNode.position.x + (errorEdge ? 300 : 200),
          y: dto.fromNode.position.y + 4,
        }
        : {
          x: targetPos.x,
          y: targetPos.y,
        },

      data: {
        node_type: dto.fromNode.data.node_type,
      } as any,
    }
    const newEdge: BaseEdgeType = {
      id: `ghost-edge`,
      source: dto.fromNode.id,
      target: id,
      ...getNewEdgeProps({ errorEdge }),
      sourceHandle: dto.fromHandle.id,
      ...(errorEdge ? { data: { startLabel: "Failed" } } : {}),
    } as BaseEdgeType

    props.setNodes((nodes) => nodes.concat(newNode))
    props.setEdges((edges) => addEdge(newEdge, edges))

    store.scenario.actions.setSelectedNode({ node_type: "add-action", node: newNode })
  }

  const replaceGhostNode = (actionType: string) => {
    const newNode = mountNewNode(
      {
        position: { x: 0, y: 0 },
        nodeType: actionType,
      },
    )

    props.setNodes((nds) =>
      nds.map((n) =>
        n.id === store.scenario.editing.selectedNode?.node?.id ? { ...newNode, position: { x: n.position.x - 3, y: n.position.y - 4 } } : n,
      ),
    )

    const fromNodeAction = store.scenario.nodeDefinitions.record[store.scenario.editing.selectedNode?.node?.data?.node_type!]
    const ghostEdge = props.edges.find((edge) => edge.id === "ghost-edge")

    const newEdge = {
      id: nanoid(8),
      target: newNode.id,
      ...getNewEdgeProps({ color: fromNodeAction.color, errorEdge: ghostEdge.sourceHandle === "error_handle" }),
    }

    props.setEdges((edges) => edges.map((edge) => (edge.id === "ghost-edge" ? { ...edge, ...newEdge } : edge)))
    store.scenario.actions.setSelectedNode(null)
    syncHistory()
  }

  const deleteGhostNode = () => {
    props.setNodes((nds) => nds.filter((n) => n.id !== "ghost").map((node) => ({ ...node, selected: false })))
    props.setEdges((edges) => edges.filter((edge) => edge.id !== "ghost-edge"))
  }

  return {
    drafted,
    setDrafted,
    history,
    onLayout,
    syncHistory,
    // syncForm,
    createGhostNode,
    replaceGhostNode,
    deleteGhostNode,
    handleDeselectAllNodes,
    handlePublish,
    preparePublish,
    inited,
    mountNewNode,
    getSafeLabel
  }
}

export default useScenarioHelpers
